import { useEffect } from "react";

/**
 * Lightweight SEO helper. Sets document.title, meta description, canonical,
 * Open Graph and Twitter Card tags per page. No external deps.
 *
 * The origin for canonical/OG URLs is derived at runtime from
 * `window.location.origin`, so the tags always reflect the domain the user
 * is actually browsing on (custom domain, staging, or preview).
 *
 * Usage:
 *   <Seo title="Marketplace | VenturaZ" description="..." />
 */
const DEFAULT_TITLE = "VenturaZ | Premium Private Market Intelligence Platform";
const DEFAULT_DESCRIPTION =
  "VenturaZ is India's premium Private Market Intelligence platform with AI-powered match scoring, curated deal rooms, secure document vaults, and end-to-end due diligence.";
const DEFAULT_IMAGE_PATH = "/og-image.png";

function upsertMeta({ name, property, content }) {
  if (!content) return null;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink({ rel, href }) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export default function Seo({
  title,
  description,
  keywords,
  image,
  path,
  type = "website",
  noindex = false,
}) {
  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const pageTitle = title || DEFAULT_TITLE;
    const pageDescription = description || DEFAULT_DESCRIPTION;
    const pageImage = image || `${origin}${DEFAULT_IMAGE_PATH}`;
    const pagePath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonical = `${origin}${pagePath}`;

    const prevTitle = document.title;
    document.title = pageTitle;

    upsertMeta({ name: "description", content: pageDescription });
    if (keywords) upsertMeta({ name: "keywords", content: keywords });
    upsertMeta({ name: "robots", content: noindex ? "noindex, nofollow" : "index, follow" });
    upsertLink({ rel: "canonical", href: canonical });

    // Open Graph
    upsertMeta({ property: "og:title", content: pageTitle });
    upsertMeta({ property: "og:description", content: pageDescription });
    upsertMeta({ property: "og:type", content: type });
    upsertMeta({ property: "og:url", content: canonical });
    upsertMeta({ property: "og:image", content: pageImage });
    upsertMeta({ property: "og:site_name", content: "VenturaZ" });

    // Twitter
    upsertMeta({ name: "twitter:card", content: "summary_large_image" });
    upsertMeta({ name: "twitter:title", content: pageTitle });
    upsertMeta({ name: "twitter:description", content: pageDescription });
    upsertMeta({ name: "twitter:image", content: pageImage });

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, keywords, image, path, type, noindex]);

  return null;
}
